import React, { useState, useEffect } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useNavigate } from 'react-router-dom';
import {
    Show,
    TextField,
    EmailField,
    DateField,
    EditButton,
    SingleFieldList,
    ArrayField,
    Datagrid,
    Link,
    useRecordContext,
    TopToolbar,
    Button,
    useDataProvider,
    useNotify,
    TabbedShowLayout,
    Tab,
    Loading
} from 'react-admin';
import { CardActions, Stack, Card, CardContent, Typography, Box, Chip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { usePermissions } from '../../auth/PermissionsContext';
import UserTitle from './fields/userTitle.component';
import YesNoField from '../../material/yesNoField.component';
import ConditionalYesNoField from './fields/ConditionalYesNoField.component';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import { DATE_OPTIONS, DATE_TIME_OPTIONS } from '../../app/app.component';
import { userFields, userRelativeFields } from '../user.model';
import OrganizationLinkField from './fields/OrganizationLinkField.component';
import OrganizationCodeLinkField from './fields/OrganizationCodeLinkField.component';
import GiftsField from './fields/GiftLinkField.component';
import UserRoleField from './fields/userRoleField.component';
import { PATIENT_RESOURCE_NAME as PATIENT_RESOURCE } from "../patient.dataProvider";
import { PROVIDER_RESOURCE_NAME as PROVIDER_RESOURCE } from "../provider.dataProvider";
import { ADMIN_RESOURCE_NAME as ADMIN_RESOURCE } from "../admin.dataProvider";
import ImpersonationReasonDialog from './modals/ImpersonationReasonDialog.component';
import ImpersonationQRDialog from './modals/ImpersonationQRDialog.component';
import { 
    validateImpersonationEligibility, 
    formatImpersonationRequest, 
    formatImpersonationError,
    validateDeepLink,
    getPatientDisplayInfo 
} from './utils/impersonationHelpers';
import { FeatureFlags } from '../../constants/featureFlags';
import { SectionedShowLayout, LabeledField } from '../../components/layouts';
import HealthieDataTab from './HealthieDataTab.component';
import ProviderClientsTab from './ProviderClientsTab.component';
import ProviderScheduleTab from './ProviderScheduleTab.component';
import ProviderPermissionsTab from './ProviderPermissionsTab.component';
import { LinkHealthieModal } from './LinkHealthieModal.component';
import { UnlinkHealthieModal } from './UnlinkHealthieModal.component';

const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const UserShowActions = () => {
    const record = useRecordContext();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    
    // Check if patient impersonation is enabled via feature flag
    const isImpersonationEnabled = useFeatureIsOn(FeatureFlags.ENABLE_PATIENT_IMPERSONATION);
    
    // Check if Healthie enrichment features are enabled
    const isHealthieEnrichmentEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_HEALTHIE_ENRICHMENT);
    
    // Impersonation state
    const [isReasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [isQRDialogOpen, setQRDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [deepLink, setDeepLink] = useState('');
    
    // Healthie linking state
    const [isHealthieLinkDialogOpen, setHealthieLinkDialogOpen] = useState(false);
    const [isHealthieUnlinkDialogOpen, setHealthieUnlinkDialogOpen] = useState(false);
    
    if (!record) return null;

    const resourcePath = getResourcePath(record);

    // Validate impersonation eligibility
    const impersonationValidation = validateImpersonationEligibility(record);
    const canImpersonate = isImpersonationEnabled && impersonationValidation.isValid;
    const impersonationDisabledReason = !isImpersonationEnabled 
        ? 'Patient impersonation feature is currently disabled' 
        : impersonationValidation.reason;

    const handleImpersonateClick = () => {
        if (canImpersonate) {
            setReasonDialogOpen(true);
        } else {
            notify(impersonationDisabledReason, { type: 'warning' });
        }
    };

    const handleImpersonationConfirm = async (reason) => {
        setIsLoading(true);
        try {
            const requestData = formatImpersonationRequest(record, reason);
            
            const response = await dataProvider.impersonate('users', { data: requestData });
            
            if (response.data && response.data.deep_link) {
                if (validateDeepLink(response.data.deep_link)) {
                    setDeepLink(response.data.deep_link);
                    setReasonDialogOpen(false);
                    setQRDialogOpen(true);
                    notify('Impersonation session created successfully', { type: 'success' });
                } else {
                    throw new Error('Invalid deep link received from server');
                }
            } else {
                throw new Error('No deep link received from server');
            }
        } catch (error) {
            const errorMessage = formatImpersonationError(error);
            notify(errorMessage, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseDialogs = () => {
        setReasonDialogOpen(false);
        setQRDialogOpen(false);
        setDeepLink('');
    };

    // Healthie linking - show Link for unlinked, show Unlink for linked providers/admins
    const isProviderOrAdmin = record.isProvider || record.isAdmin;
    // Check both snake_case and camelCase versions of healthie_id
    const hasHealthieId = record.healthieId || record.healthie_id;
    const canLinkHealthie = isHealthieEnrichmentEnabled && isProviderOrAdmin && !hasHealthieId;
    const canUnlinkHealthie = isHealthieEnrichmentEnabled && isProviderOrAdmin && hasHealthieId;

    const handleUnlinkHealthie = () => {
        setHealthieUnlinkDialogOpen(true);
    };

    const patientInfo = getPatientDisplayInfo(record);

    return (
        <>
            <TopToolbar>
                <EditButton />
                <Button
                    label="Change Password"
                    onClick={() => navigate(`/${resourcePath}/${record.id}/changePassword`)}
                />
                {canLinkHealthie && (
                    <Button
                        label="Link to Healthie"
                        onClick={() => setHealthieLinkDialogOpen(true)}
                    />
                )}
                {canUnlinkHealthie && (
                    <Button
                        label="Unlink from Healthie"
                        onClick={handleUnlinkHealthie}
                        color="warning"
                    />
                )}
                {record.isPatient && (
                    <Button
                        label="Impersonate"
                        onClick={handleImpersonateClick}
                        disabled={!canImpersonate}
                        title={!canImpersonate ? impersonationDisabledReason : 'Impersonate this patient'}
                    />
                )}
            </TopToolbar>
            
            {/* Link Healthie Dialog */}
            <LinkHealthieModal
                open={isHealthieLinkDialogOpen}
                onClose={() => setHealthieLinkDialogOpen(false)}
                userId={record.id}
                userEmail={record.email}
                userName={`${record.first_name} ${record.last_name}`}
                isProvider={record.isProvider}
                isAdmin={record.isAdmin}
            />
            
            {/* Unlink Healthie Dialog */}
            <UnlinkHealthieModal
                open={isHealthieUnlinkDialogOpen}
                onClose={() => setHealthieUnlinkDialogOpen(false)}
                userId={record.id}
                userEmail={record.email}
                userName={`${record.first_name} ${record.last_name}`}
                healthieId={record.healthieId || record.healthie_id}
                isProvider={record.isProvider}
                isAdmin={record.isAdmin}
            />
            
            {/* Impersonation Dialogs */}
            <ImpersonationReasonDialog
                open={isReasonDialogOpen}
                onClose={handleCloseDialogs}
                onConfirm={handleImpersonationConfirm}
                patient={patientInfo}
                isLoading={isLoading}
            />
            
            <ImpersonationQRDialog
                open={isQRDialogOpen}
                onClose={handleCloseDialogs}
                deepLink={deepLink}
                patient={patientInfo}
            />
        </>
    );
};

const getResourcePath = (record) => {
    if (record.isPatient) return PATIENT_RESOURCE;
    if (record.isProvider) return PROVIDER_RESOURCE;
    if (record.isAdmin) return ADMIN_RESOURCE;
    return PATIENT_RESOURCE; // default to patient resource since all users should have a type
};

const StyledLink = styled(Link)({
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'none'
    }
});

const ProviderFields = () => {
    const record = useRecordContext();
    const isProvider = record && record.isProvider;

    if (!isProvider) return null;

    return (
        <>
            <LabeledField key="providerBio" label="Provider Bio">
                <DefaultPlaceholderField source={userFields.providerBio} wrapField={<TextField />} />
            </LabeledField>
            <LabeledField key="providerBioEs" label="Provider Bio (Spanish)">
                <DefaultPlaceholderField source={userFields.providerBioEs} wrapField={<TextField/>} />
            </LabeledField>
        </>
    );
};

const PatientFields = () => {
    const record = useRecordContext();
    const isPatient = record && record.isPatient;

    if (!isPatient) return null;

    return (
        <>
            <LabeledField key="dueDate" label="Due Date">
                <DefaultPlaceholderField source={userFields.dueDate} options={DATE_OPTIONS} wrapField={<DateField/>} />
            </LabeledField>
            <LabeledField key="birthWish" label="Birth Wish List">
                <DefaultPlaceholderField source={userFields.birthWish} wrapField={<TextField/>} />
            </LabeledField>
            <LabeledField key="birthPartner" label="Birth Partner">
                <DefaultPlaceholderField source={userFields.birthPartner} wrapField={<TextField/>} />
            </LabeledField>
            <LabeledField key="prenatalVisit" label="Prenatal Visits">
                <DefaultPlaceholderField source={userFields.prenatalVisit} wrapField={<TextField/>} />
            </LabeledField>
        </>
    );
};

const AdminFields = () => {
    const record = useRecordContext();
    const isAdmin = record && record.isAdmin;

    if (!isAdmin) return null;

    return (
        <>
            <LabeledField key="mfaRequired" label="Dashboard MFA Required">
                <YesNoField source={userFields.isMfaRequired} />
            </LabeledField>
        </>
    );
};

const ProviderAdminFields = () => {
    const record = useRecordContext();
    const isProviderOrAdmin = record && (record.isProvider || record.isAdmin);

    if (!isProviderOrAdmin) return null;

    return (
        <>
            <LabeledField key="permissionTemplate" label="Healthie Permission Template">
                <DefaultPlaceholderField source={userFields.permissionTemplateName} wrapField={<TextField/>} />
            </LabeledField>
        </>
    );
};

const ConditionalRoleFields = () => {
    const record = useRecordContext();
    if (!record) return null;

    const fields = [];

    // Provider/Admin fields (only show if they have Healthie membership)
    // Check both healthieId (camelCase) and healthie_id (snake_case from API)
    const hasHealthieId = record.healthieId || record.healthie_id;
    if ((record.isProvider || record.isAdmin) && hasHealthieId) {
        // DEBUG: Log what we have
        console.log('🔍 Template Permission Debug:', {
            userId: record.id,
            hasHealthieData: !!record.healthie_data,
            healthieDataKeys: record.healthie_data ? Object.keys(record.healthie_data) : null,
            hasOrgMembership: !!record.healthie_data?.organization_membership,
            hasPermissionTemplate: !!record.healthie_data?.organization_membership?.permission_template,
            templateFromNested: record.healthie_data?.organization_membership?.permission_template,
            templateFromTopLevel: {
                id: record.template_permission_id,
                name: record.template_permission_name
            }
        });
        
        // Get template permission from nested healthie_data or top level
        const templateName = record.healthie_data?.organization_membership?.permission_template?.name || 
                           record.template_permission_name || 
                           'n/a';
        
        fields.push(
            <LabeledField key="permissionTemplate" label="Healthie Permission Template">
                <span>{templateName}</span>
            </LabeledField>
        );
    }

    // Provider-only fields
    if (record.isProvider) {
        fields.push(
            <LabeledField key="providerBio" label="Provider Bio">
                <DefaultPlaceholderField source={userFields.providerBio} wrapField={<TextField />} />
            </LabeledField>,
            <LabeledField key="providerBioEs" label="Provider Bio (Spanish)">
                <DefaultPlaceholderField source={userFields.providerBioEs} wrapField={<TextField/>} />
            </LabeledField>
        );
    }

    // Patient-only fields
    if (record.isPatient) {
        fields.push(
            <LabeledField key="dueDate" label="Due Date">
                <DefaultPlaceholderField source={userFields.dueDate} options={DATE_OPTIONS} wrapField={<DateField/>} />
            </LabeledField>,
            <LabeledField key="birthWish" label="Birth Wish List">
                <DefaultPlaceholderField source={userFields.birthWish} wrapField={<TextField/>} />
            </LabeledField>,
            <LabeledField key="birthPartner" label="Birth Partner">
                <DefaultPlaceholderField source={userFields.birthPartner} wrapField={<TextField/>} />
            </LabeledField>,
            <LabeledField key="prenatalVisit" label="Prenatal Visits">
                <DefaultPlaceholderField source={userFields.prenatalVisit} wrapField={<TextField/>} />
            </LabeledField>
        );
    }

    // Admin-only fields
    if (record.isAdmin) {
        fields.push(
            <LabeledField key="mfaRequired" label="Dashboard MFA Required">
                <YesNoField source={userFields.isMfaRequired} />
            </LabeledField>
        );
    }

    return <>{fields}</>;
};

// Healthie Tab Components for Patients
const SchedulingTab = () => {
    const record = useRecordContext();
    const healthieData = record?.healthie_data;

    if (!healthieData) return <Typography variant="body2" color="text.secondary">No Healthie data available</Typography>;

    // Filter credits with quantity > 0
    const activeCredits = healthieData.appointment_type_credits?.filter(c => c.quantity && parseInt(c.quantity) > 0) || [];
    const appointments = healthieData.appointments || [];

    return (
        <Box sx={{ p: 2 }}>
            {/* Appointment Credits Section */}
            <Typography variant="h6" gutterBottom>Appointment Credits</Typography>
            {activeCredits.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No active credits</Typography>
            ) : (
                <Box sx={{ mb: 4 }}>
                    {activeCredits.map((credit, index) => (
                        <Box key={index} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">{credit.appointment_type?.name}</Typography>
                            <Chip label={`${credit.quantity} credits`} size="small" color="success" />
                        </Box>
                    ))}
                </Box>
            )}

            {/* Appointment History Section */}
            <Typography variant="h6" gutterBottom>Appointment History</Typography>
            {appointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No appointments</Typography>
            ) : (
                <Box>
                    {appointments.map((appt, index) => (
                        <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < appointments.length - 1 ? '1px solid #eee' : 'none' }}>
                            <Typography variant="body2"><strong>{new Date(appt.date).toLocaleString()}</strong></Typography>
                            <Typography variant="body2">{appt.appointment_type?.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Provider: {appt.providers?.[0]?.full_name || 'N/A'}
                                {appt.pm_status && ` • Status: ${appt.pm_status}`}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const InsuranceTab = () => {
    const record = useRecordContext();
    const policies = record?.healthie_data?.policies || [];

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Insurance Policies</Typography>
            {policies.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No insurance policies</Typography>
            ) : (
                policies.map((policy, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>{policy.insurance_plan?.payer_name || 'Unknown Payer'}</strong></Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                            Policy: {policy.num || 'N/A'} • Group: {policy.group_num || 'N/A'}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                            Type: {policy.priority_type} • Relationship: {policy.holder_relationship}
                        </Typography>
                        {policy.policy_phone_number && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                Phone: {policy.policy_phone_number}
                            </Typography>
                        )}
                    </Box>
                ))
            )}
        </Box>
    );
};

const FormsTab = () => {
    const record = useRecordContext();
    const forms = record?.healthie_data?.form_answer_groups || [];

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Completed Forms</Typography>
            {forms.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No forms completed</Typography>
            ) : (
                forms.map((form, index) => (
                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < forms.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="body2"><strong>{form.custom_module_form?.name || form.name}</strong></Typography>
                        <Typography variant="caption" color="text.secondary">
                            Completed: {new Date(form.created_at).toLocaleDateString()}
                        </Typography>
                    </Box>
                ))
            )}
        </Box>
    );
};

const AssignedProviderTab = () => {
    const record = useRecordContext();
    const dietitian = record?.healthie_data?.dietitian;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Assigned Provider</Typography>
            {!dietitian ? (
                <Typography variant="body2" color="text.secondary">No assigned provider</Typography>
            ) : (
                <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{dietitian.full_name}</strong></Typography>
                    <Typography variant="body2" color="text.secondary">{dietitian.email}</Typography>
                    <Typography variant="caption" color="text.secondary">Healthie ID: {dietitian.id}</Typography>
                </Box>
            )}
        </Box>
    );
};

const ClientGroupTab = () => {
    const record = useRecordContext();
    const clientGroup = record?.healthie_data?.user_group;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Client Group</Typography>
            {!clientGroup ? (
                <Typography variant="body2" color="text.secondary">No client group assigned</Typography>
            ) : (
                <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{clientGroup.name}</strong></Typography>
                    <Typography variant="caption" color="text.secondary">Group ID: {clientGroup.id}</Typography>
                </Box>
            )}
        </Box>
    );
};

const UserPermissionsDisplay = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!record?.id) return;

        const fetchPermissions = async () => {
            try {
                setLoading(true);
                const response = await dataProvider.getList('users', {
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'id', order: 'ASC' },
                    filter: { id: record.id },
                    meta: { subResource: 'permissions' },
                });

                setPermissions(response.data || []);
            } catch (error) {
                console.error('Failed to fetch user permissions:', error);
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [record?.id, dataProvider]);

    if (loading) {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading permissions...</Typography>
            </Box>
        );
    }

    if (permissions.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No permissions assigned to this user
            </Typography>
        );
    }

    // Group permissions by category
    const groupedPermissions = permissions.reduce((acc, perm) => {
        const category = perm.category || 'other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(perm);
        return acc;
    }, {});

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Permissions: {permissions.length}
            </Typography>
            {Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)).map(([category, perms]) => (
                <Box key={category} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize', fontWeight: 600 }}>
                        {category} ({perms.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {perms.sort((a, b) => a.name.localeCompare(b.name)).map(perm => (
                            <Chip
                                key={perm.id}
                                label={perm.name}
                                size="small"
                                sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const UserShowContent = () => {
    const record = useRecordContext();
    const isRBACEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_RBAC);
    const isHealthieEnrichmentEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_HEALTHIE_ENRICHMENT);
    const { hasPermission } = usePermissions();
    
    // Wait for record to load before rendering
    if (!record) return <Loading />;
    
    // Show Permissions tab if logged-in user has permission to manage roles/permissions
    const canManagePermissions = hasPermission('admin:manage-roles-and-permissions');
    const showPermissionsTab = canManagePermissions && isRBACEnabled;
    
    const sections = [
        {
            title: "Basic Information",
            fields: [
                <LabeledField key="id" label="Id">
                    <DefaultPlaceholderField source={userFields.id} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="healthieId" label="Healthie ID">
                    <DefaultPlaceholderField source={userFields.healthieId} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="email" label="Email">
                    <DefaultPlaceholderField source={userFields.email} wrapField={<EmailField/>} />
                </LabeledField>,
                <LabeledField key="firstName" label="First Name">
                    <DefaultPlaceholderField source={userFields.firstName} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="lastName" label="Last Name">
                    <DefaultPlaceholderField source={userFields.lastName} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="dob" label="Date of Birth">
                    <DefaultPlaceholderField source={userFields.dob} options={DATE_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>
            ]
        },
        {
            title: "Contact & Location",
            fields: [
                <LabeledField key="phoneNumber" label="Phone Number">
                    <DefaultPlaceholderField source={userFields.phoneNumber} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="language" label="Language">
                    <DefaultPlaceholderField source={userFields.lang} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="state" label="State">
                    <DefaultPlaceholderField source={userFields.usState} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="zipCode" label="Zip Code">
                    <DefaultPlaceholderField source={userFields.zipCode} wrapField={<TextField/>} />
                </LabeledField>
            ]
        },
        {
            title: "Organization & Role",
            fields: [
                <LabeledField key="organization" label="Organization">
                    <OrganizationLinkField />
                </LabeledField>,
                <LabeledField key="role" label="Role">
                    <UserRoleField />
                </LabeledField>,
                <LabeledField key="wicId" label="WIC ID">
                    <DefaultPlaceholderField source={userFields.wicId} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="orgCodes" label="Organization Codes">
                    <OrganizationCodeLinkField />
                </LabeledField>
            ]
        },
        {
            title: "Role-Specific Information",
            fields: [
                <ConditionalRoleFields key="roleFields" />
            ]
        },
        {
            title: "System Information",
            fields: [
                <LabeledField key="isActive" label="Is Active">
                    <YesNoField source={userFields.isActive} />
                </LabeledField>,
                <LabeledField key="createdAt" label="Created At">
                    <DefaultPlaceholderField source={userFields.createdAt} options={DATE_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>,
                <LabeledField key="updatedAt" label="Updated At">
                    <DefaultPlaceholderField source={userFields.updatedAt} options={DATE_TIME_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>
            ]
        }
    ];

    if (!showPermissionsTab) {
        return <SectionedShowLayout sections={sections} />;
    }

    // Check for Healthie data to conditionally show Healthie tabs
    const healthieData = record?.healthie_data;
    // Only show tabs if we have actual data (not just healthie_id) AND feature flag is enabled
    const hasHealthieData = isHealthieEnrichmentEnabled && healthieData && typeof healthieData === 'object' && Object.keys(healthieData).length > 0;
    const roleName = record?.role_name?.toLowerCase() || '';
    
    // Determine user type based on role
    const isPatient = roleName === 'patient';
    const isProvider = roleName.includes('provider') || 
                      roleName.includes('dietitian') ||
                      roleName.includes('lactation') ||
                      roleName.includes('doula');
    const isAdmin = roleName.includes('admin');

    return (
        <TabbedShowLayout syncWithLocation={false}>
            <Tab label="Details" path="details">
                <SectionedShowLayout sections={sections} />
            </Tab>
            <Tab label="Permissions" path="permissions">
                <UserPermissionsDisplay />
            </Tab>
            
            {/* PATIENT-SPECIFIC HEALTHIE TABS */}
            {hasHealthieData && isPatient && (
                <Tab label="Appointments" path="appointments">
                    <SchedulingTab />
                </Tab>
            )}
            {hasHealthieData && isPatient && (
                <Tab label="Insurance" path="insurance">
                    <InsuranceTab />
                </Tab>
            )}
            {hasHealthieData && isPatient && (
                <Tab label="Forms" path="forms">
                    <FormsTab />
                </Tab>
            )}
            {hasHealthieData && isPatient && (
                <Tab label="Care Team" path="care-team">
                    <AssignedProviderTab />
                </Tab>
            )}
            {hasHealthieData && isPatient && (
                <Tab label="Organization" path="organization">
                    <ClientGroupTab />
                </Tab>
            )}
            
            {/* PROVIDER-SPECIFIC HEALTHIE TABS */}
            {hasHealthieData && (isProvider || isAdmin) && (
                <Tab label="Clients" path="clients">
                    <ProviderClientsTab />
                </Tab>
            )}
            {hasHealthieData && (isProvider || isAdmin) && (
                <Tab label="Schedule" path="schedule">
                    <ProviderScheduleTab />
                </Tab>
            )}
            {hasHealthieData && (isProvider || isAdmin) && (
                <Tab label="Healthie Permissions" path="healthie-permissions">
                    <ProviderPermissionsTab />
                </Tab>
            )}
        </TabbedShowLayout>
    );
};

const UserShow = (props) => {
    window.scrollTo(0, 0);

    return (
        <Show 
            title={<UserTitle style={{verticalAlign: 'middle'}}/>} 
            actions={<UserShowActions />}
            queryOptions={{ 
                refetchOnMount: 'always',  // Always fetch fresh data when navigating to show page
                staleTime: 0  // Consider data stale immediately to force refetch
            }}
            {...props}
        >
            <UserShowContent />
        </Show>
    );
};

export default UserShow;
